import { Box, Button, Stack, Typography } from '@mui/material'
import { useOnboarding } from '../context/OnboardingContext'
import { FileChip, UploadButton, toFileRef } from './ui'
import { authorizationStatesNeeded } from '../lib/completion'
import type { AuthorizationState } from '../types/onboarding'

export const AUTHORIZATION_LABELS: Record<AuthorizationState, string> = {
  CA: 'California Authorization Form',
  ID: 'Idaho Authorization Form',
  TX: 'Texas Authorization Letter',
}

const SIGNATURE_ACCEPT = '.pdf,.jpg,.jpeg,.png,application/pdf,image/jpeg,image/png'

function UploadRow({
  label,
  hint,
  children,
}: {
  label: string
  hint?: string
  children: React.ReactNode
}) {
  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: 1.5, borderRadius: 2, border: '1px solid #e2e8f0', p: 1.75 }}>
      <Box sx={{ minWidth: 0 }}>
        <Typography sx={{ fontSize: 14, fontWeight: 600, color: '#0f172a' }}>{label}</Typography>
        {hint ? <Typography sx={{ fontSize: 12, color: '#64748b' }}>{hint}</Typography> : null}
      </Box>
      {/* minWidth/maxWidth let a long file name truncate instead of overflowing the row */}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1, minWidth: 0, maxWidth: '100%' }}>{children}</Box>
    </Box>
  )
}

export function SignatureUpload() {
  const { state, dispatch } = useOnboarding()
  const file = state.implementation.signature
  return (
    <UploadRow label="Digital Signature" hint="Upload a quality digital or scanned signature. PDF, JPG, or PNG.">
      {file ? <FileChip file={file} onRemove={() => dispatch({ type: 'SET_SIGNATURE', file: null })} /> : null}
      <UploadButton
        label={file ? 'Replace' : 'Upload'}
        accept={SIGNATURE_ACCEPT}
        onFiles={(files) => dispatch({ type: 'SET_SIGNATURE', file: toFileRef(files[0]) })}
      />
    </UploadRow>
  )
}

/** Third-party filer authorizations — only for CA / ID / TX when an entity has MISC handling that state. */
export function AuthorizationUploads() {
  const { state, dispatch } = useOnboarding()
  const needed = authorizationStatesNeeded(state)

  if (needed.length === 0) {
    return (
      <Typography sx={{ fontSize: 13, color: '#64748b' }}>
        None needed — no entity has selected California, Idaho, or Texas.
      </Typography>
    )
  }

  return (
    <Stack spacing={1.25}>
      {needed.map((code) => {
        const file = state.implementation.authorizations[code]
        return (
          <UploadRow key={code} label={AUTHORIZATION_LABELS[code]} hint="Form link to be provided by MISC.">
            {file ? <FileChip file={file} onRemove={() => dispatch({ type: 'SET_AUTHORIZATION', stateCode: code, file: null })} /> : null}
            <UploadButton
              label={file ? 'Replace' : 'Upload'}
              accept=".pdf,.jpg,.jpeg,.png"
              onFiles={(files) => dispatch({ type: 'SET_AUTHORIZATION', stateCode: code, file: toFileRef(files[0]) })}
            />
          </UploadRow>
        )
      })}
    </Stack>
  )
}

export const OTR_ACCEPT = '.csv,.xls,.xlsx,.xml,text/csv,application/xml,text/xml'

/** MISC's OTR template (the one Teri sends clients): "Template" tab with required fields, "Example" tab with sample rows. */
const OTR_SAMPLE_URL = '/assets/onboarding/Tax-Transaction-Report-OTR-Template.xlsx'

export function OtrSampleDownload() {
  return (
    <UploadRow label="Sample Open Tax Report Template" hint="See the required fields and layout before preparing your report.">
      <Button
        component="a"
        href={OTR_SAMPLE_URL}
        download
        variant="outlined"
        size="small"
        sx={{ borderColor: '#cbd5e1', color: '#334155' }}
      >
        Download Sample
      </Button>
    </UploadRow>
  )
}

export function OtrUpload() {
  const { state, dispatch } = useOnboarding()
  const file = state.otrUpload
  return (
    <UploadRow label="Sample Open Tax Report" hint="Supported formats: CSV, Excel, XML.">
      {file ? <FileChip file={file} onRemove={() => dispatch({ type: 'SET_OTR_UPLOAD', file: null })} /> : null}
      <UploadButton
        label={file ? 'Replace' : 'Upload OTR'}
        variant={file ? 'outlined' : 'contained'}
        accept={OTR_ACCEPT}
        onFiles={(files) => dispatch({ type: 'SET_OTR_UPLOAD', file: toFileRef(files[0]) })}
      />
    </UploadRow>
  )
}
